import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowDropDownCircle as ArrowDropDownCircleIcon,
  CancelRounded as CancelRoundedIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import moment from 'moment';
import 'moment/locale/fr-ca';
import 'moment/locale/en-ca';
import {
  COLOR_BLACK_HAZE,
  COLOR_BLACK_PERL,
  COLOR_DENIM,
  COLOR_GALLERY,
  COLOR_GOLDEN_TAINOI,
  COLOR_KOBI,
  COLOR_LIGHT_GREY,
  COLOR_MONTE_CARLO,
  COLOR_PALE_SKY,
  COLOR_REGENT_ST_BLUE,
  COLOR_SAIL,
  COLOR_SALMON,
  FONT_SIZE_16_PX,
  FONT_WEIGHT_SEMI_BOLD,
} from 'popTheme';
import { TrendsGraph } from './TrendsGraph';
import { DayGraph } from './DayGraph';
import { ResultCategories } from './Results';
import { selectResults, selectResultsSelectedDate } from 'redux/reducers/results';
import { selectProfile } from 'redux/reducers/session';
import { updateResultsSelectedDate } from 'redux/sagas/updateResultsSaga';
import { sendClientAnalytics } from 'redux/sagas/clientAnalyticsSaga';
import { AuditEventAction, createClientAnalytics } from '../../ClientAnalytics';

export const GraphType = {
  TREND: 'TREND',
  DAY: 'DAY',
};

// The only way to be able to test the XYChart is to allow the tests to provide a width: https://github.com/airbnb/visx/issues/951#issuecomment-785305999
export const ResultsGraphSection = ({ chartWidth, isMobile }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const results = useSelector(selectResults);
  const selectedDate = useSelector(selectResultsSelectedDate);
  const [dates, setDates] = useState(['']);
  const [filteredResults, setFilteredResults] = useState(results);
  const [graphType, setGraphType] = useState(GraphType.DAY);
  const currentProfile = useSelector(selectProfile);
  const language = currentProfile?.language;

  useEffect(() => {
    const resultDates = results.map((result) => result.date);
    setDates(resultDates);
    setFilteredResults(results);
  }, [results]);

  useEffect(() => {
    if (dates?.length > 0) {
      dispatch(updateResultsSelectedDate(dates[dates.length - 1]));
    }
  }, [dates, dispatch]);

  const messages = defineMessages({
    resultsButtonTrends: {
      id: 'results.button.trends',
      defaultMessage: 'Trends',
      description: 'Results page trends button text',
    },
    resultsButtonDay: {
      id: 'results.button.Day',
      defaultMessage: "Day's Results",
      description: 'Results page days results button text',
    },
    resultsButtonPain: {
      id: 'results.button.pain',
      defaultMessage: 'Pain',
      description: 'Results page pain button text',
    },
    resultsButtonWorry: {
      id: 'results.button.worry',
      defaultMessage: 'Worry',
      description: 'Results page worry button text',
    },
    resultsButtonMood: {
      id: 'results.button.mood',
      defaultMessage: 'Mood',
      description: 'Results page mood button text',
    },
    resultsButtonSleep: {
      id: 'results.button.sleep',
      defaultMessage: 'Sleep',
      description: 'Results page sleep button text',
    },
    resultsButtonSubstanceUse: {
      id: 'results.button.substance_use',
      defaultMessage: 'Substance Use',
      description: 'Results page substance use button text',
    },
    resultsShowAll: {
      id: 'results.button.show_all',
      defaultMessage: 'Show All',
      description: 'Results page show all button text',
    },
    resultsButtonGroup: {
      id: 'results.button.group',
      defaultMessage: 'Show / Hide Data',
      description: 'Results page drop down button text',
    },
  });

  const handleChangeDate = (e) => {
    dispatch(updateResultsSelectedDate(e.target.value));
  };

  const handleDateForward = () => {
    const selectedDateIndex = dates.indexOf(selectedDate);
    // Makes sure there is another date.  If so, select it.
    if (selectedDateIndex === -1) {
      dispatch(updateResultsSelectedDate(dates[1]));
    } else if (selectedDateIndex < dates.length - 1) {
      dispatch(updateResultsSelectedDate(dates[selectedDateIndex + 1]));
    }
  };

  const handleDateBackward = () => {
    const selectedDateIndex = dates.indexOf(selectedDate);
    // Makes sure there is another date.  If so, select it.
    if (selectedDateIndex === -1) {
      dispatch(updateResultsSelectedDate(dates[dates.length - 1]));
    } else if (selectedDateIndex > 0) {
      dispatch(updateResultsSelectedDate(dates[selectedDateIndex - 1]));
    }
  };

  const createAuditEvent = (category, isVisible) => {
    dispatch(
      sendClientAnalytics(
        createClientAnalytics(
          AuditEventAction.RESULT_FILTERS.category,
          AuditEventAction.RESULT_FILTERS.action,
          {
            category: category,
            visible: isVisible,
          },
        ),
      ),
    );
  };

  const handleGraphSectionButtonClick = (ScoreKey) => {
    if (ScoreKey === 'showAll') {
      setFilteredResults(results); // Show all results
    } else {
      setFilteredResults((prevResults) =>
        prevResults.map((result) => {
          if (result.hasOwnProperty(ScoreKey)) {
            const { [ScoreKey]: removedProperty, ...rest } = result;
            createAuditEvent(ScoreKey, false);
            return rest; // Filter out the ScoreKey from the object
          } else {
            const actualResult = results.find((r) => r.date === result.date); // Find the corresponding result object from the actual results
            createAuditEvent(ScoreKey, true);
            return { ...result, [ScoreKey]: actualResult[ScoreKey] }; // Property doesn't exist, add it back with the actual value
          }
        }),
      );
    }
  };

  const createGraphyTypeAuditEvent = (type) => {
    dispatch(
      sendClientAnalytics(
        createClientAnalytics(
          AuditEventAction.RESULT_GRAPH.category,
          AuditEventAction.RESULT_GRAPH.action,
          {
            graph: type,
          },
        ),
      ),
    );
  };

  const handleGraphTypeChanged = (e) => {
    const { value } = e.target || e;
    if (value !== graphType) {
      setGraphType(value);
      createGraphyTypeAuditEvent(value);
    }
  };

  const getButtonBackground = (propertyName, backgroundColor) => {
    return filteredResults.some((result) => !result.hasOwnProperty(propertyName))
      ? COLOR_LIGHT_GREY
      : backgroundColor;
  };

  const graphTypeButtonStyle = {
    backgroundColor: COLOR_GALLERY,
    color: COLOR_DENIM,
    height: '2.75rem',
    borderRadius: '0.375rem',
    fontWeight: FONT_WEIGHT_SEMI_BOLD,
    fontSize: FONT_SIZE_16_PX,
    py: '1.5rem',
    ':hover': {
      backgroundColor: COLOR_GALLERY,
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      color: COLOR_GALLERY,
      backgroundColor: COLOR_DENIM,
    },
  };

  const GraphSectionButton = ({ background, color, text, onClick, value }) => {
    return (
      <ToggleButton
        value={value}
        variant="contained"
        onClick={onClick}
        sx={{
          background,
          color,
          height: '2.75rem',
          borderRadius: '0.375rem',
          fontWeight: FONT_WEIGHT_SEMI_BOLD,
          fontSize: FONT_SIZE_16_PX,
          px: '0.625rem',
          py: '1.5rem',
          mr: { xs: '0rem', sm: '1.5rem' },
          ':hover': {
            backgroundColor: COLOR_PALE_SKY,
          },
        }}
      >
        {text}
      </ToggleButton>
    );
  };

  const GraphSectionButtonGroup = () => {
    return (
      <>
        <GraphSectionButton
          background={getButtonBackground(ResultCategories.PAIN, COLOR_SAIL)}
          color={COLOR_BLACK_PERL}
          text={intl.formatMessage(messages.resultsButtonPain)}
          onClick={() => handleGraphSectionButtonClick(ResultCategories.PAIN)}
          value={ResultCategories.PAIN}
        />
        <GraphSectionButton
          background={getButtonBackground(ResultCategories.WORRY, COLOR_SALMON)}
          color={COLOR_BLACK_PERL}
          text={intl.formatMessage(messages.resultsButtonWorry)}
          onClick={() => handleGraphSectionButtonClick(ResultCategories.WORRY)}
          value={ResultCategories.WORRY}
        />
        <GraphSectionButton
          background={getButtonBackground(ResultCategories.MOOD, COLOR_GOLDEN_TAINOI)}
          color={COLOR_BLACK_PERL}
          text={intl.formatMessage(messages.resultsButtonMood)}
          onClick={() => handleGraphSectionButtonClick(ResultCategories.MOOD)}
          value={ResultCategories.MOOD}
        />
        <GraphSectionButton
          background={getButtonBackground(ResultCategories.SLEEP, COLOR_KOBI)}
          color={COLOR_BLACK_PERL}
          text={intl.formatMessage(messages.resultsButtonSleep)}
          onClick={() => handleGraphSectionButtonClick(ResultCategories.SLEEP)}
          value={ResultCategories.SLEEP}
        />
        <GraphSectionButton
          background={getButtonBackground(ResultCategories.SUBSTANCE, COLOR_MONTE_CARLO)}
          color={COLOR_BLACK_PERL}
          text={intl.formatMessage(messages.resultsButtonSubstanceUse)}
          onClick={() => handleGraphSectionButtonClick(ResultCategories.SUBSTANCE)}
          value={ResultCategories.SUBSTANCE}
        />
      </>
    );
  };

  const GraphSectionButtonDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = () => {
      setIsOpen(!isOpen);
    };

    let dropdownContents = null;

    if (isOpen) {
      dropdownContents = (
        <ButtonGroup orientation="vertical" fullWidth sx={{ gap: '0.75rem' }}>
          <Divider sx={{ border: `0.0625rem solid ${COLOR_DENIM}` }} />
          <GraphSectionButtonGroup />
          <Divider sx={{ border: `0.0625rem solid ${COLOR_DENIM}` }} />
          <GraphSectionButton
            background={COLOR_DENIM}
            color={COLOR_GALLERY}
            text={intl.formatMessage(messages.resultsShowAll)}
            onClick={() => handleGraphSectionButtonClick('showAll')}
            value={'showAll'}
          />
        </ButtonGroup>
      );
    }

    return (
      <Box
        sx={{
          backgroundColor: COLOR_BLACK_HAZE,
        }}
      >
        <Button
          variant="text"
          onClick={handleToggle}
          fullWidth
          startIcon={<SortIcon sx={{ color: COLOR_DENIM }} />}
          endIcon={
            isOpen ? (
              <CancelRoundedIcon sx={{ color: COLOR_DENIM }} />
            ) : (
              <ArrowDropDownCircleIcon sx={{ color: COLOR_DENIM }} />
            )
          }
          sx={{
            color: COLOR_DENIM,
            justifyContent: 'space-between',
            border: isOpen ? 'none' : `0.125rem solid ${COLOR_REGENT_ST_BLUE}`,
            borderRadius: '0.375rem',
          }}
        >
          {intl.formatMessage(messages.resultsButtonGroup)}
        </Button>
        {dropdownContents}
      </Box>
    );
  };

  return (
    <Grid container display="flex" direction="row" justifyContent={'space-between'}>
      <Grid item xs={12} textAlign="center" pb={{ xs: '2rem', sm: '3rem' }}>
        <FormControl variant="standard">
          <Typography variant="h3">
            <IconButton
              aria-label="date-back"
              onClick={handleDateBackward}
              data-testid={'results-graph-section-date-back'}
              sx={{ color: 'text.primary' }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Select
              labelId="date-select-standard-label"
              id="date-select-standard"
              value={selectedDate || ''}
              displayEmpty
              onChange={handleChangeDate}
              sx={{
                height: { xs: '1.875rem', sm: '2.9375rem' },
                width: { xs: 'auto', sm: '33.75rem' },
                minWidth: { xs: '10rem', sm: '22rem' },
                color: 'text.primary',
                ':before': { borderBottomColor: 'text.secondary' },
                ':after': { borderBottomColor: 'text.secondary' },
                '& .MuiSvgIcon-root': {
                  color: 'text.primary',
                },
                '& .MuiSelect-select .MuiTypography-root': {
                  fontSize: { xs: '0.9rem', sm: '1.9375rem' },
                  fontWeight: 'bold',
                },
              }}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: { style: { maxHeight: '15rem', overflowY: 'auto' } },
              }}
              inputProps={{ 'data-testid': 'results-graph-date-input' }}
            >
              {dates?.length > 0 ? (
                dates.map((date, index) => {
                  return (
                    <MenuItem value={date} key={`results-date-${index}`}>
                      <Typography variant="body2">
                        {language === 'FRENCH'
                          ? moment(date).locale('fr-ca').format('dddd, MMMM Do YYYY')
                          : moment(date).locale('en-ca').format('dddd, MMMM Do YYYY')}
                      </Typography>
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value="" key={'results-date-default'} />
              )}
            </Select>
            <IconButton
              aria-label="date-forward"
              onClick={handleDateForward}
              data-testid={'results-graph-section-date-forward'}
              sx={{ color: 'text.primary' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Typography>
        </FormControl>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        textAlign={{ xs: 'center', sm: 'left' }}
        pb={{ xs: '0.75rem', sm: '3rem' }}
      >
        <ToggleButtonGroup
          fullWidth={false}
          exclusive
          onChange={handleGraphTypeChanged}
          value={graphType}
          data-testid={'graph-toggle-button'}
        >
          <ToggleButton
            value={GraphType.DAY}
            key={GraphType.DAY}
            name={GraphType.DAY}
            sx={graphTypeButtonStyle}
            data-testid={'results-graph-section-day-graph-button'}
          >
            {intl.formatMessage(messages.resultsButtonDay)}
          </ToggleButton>
          <ToggleButton
            value={GraphType.TREND}
            key={GraphType.TREND}
            name={GraphType.TREND}
            sx={graphTypeButtonStyle}
            data-testid={'results-graph-section-trend-graph-button'}
          >
            {intl.formatMessage(messages.resultsButtonTrends)}
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        textAlign={{ xs: 'center', sm: 'right' }}
        pb={{ xs: '0.75rem', sm: '3rem' }}
      >
        {!isMobile ? <GraphSectionButtonGroup /> : <GraphSectionButtonDropdown />}
      </Grid>
      <Grid container py="1.25rem">
        {graphType === GraphType.DAY ? (
          <Grid item xs={12} data-testid={'day-graph'}>
            <DayGraph results={filteredResults} width={chartWidth} isMobile={isMobile} />
          </Grid>
        ) : (
          <Grid item xs={12} data-testid={'trend-graph'}>
            <TrendsGraph results={filteredResults} width={chartWidth} isMobile={isMobile} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
